import { useEffect, useState } from 'react';

import { Modal, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { unwrap } from 'pages/testrail/helpers';
import { useApp } from 'common/AppContext';

function defaultTitleFromPlan(testPlan, url) {

    const firstLine = (testPlan || '').split('\n').map(s => s.trim()).filter(Boolean)[0];
    if (firstLine) return firstLine;

    try {

        return `Functional test: ${new URL(url).hostname}`;

    } catch {

        return 'Functional test';

    }

}

function formatStepsFromPhases(phases) {

    if (!Array.isArray(phases)) return '';

    return phases.map(phase => {

        const header = `[${phase.category}] ${phase.label}`;
        const lines = (phase.steps || []).map((s, i) => `  ${i + 1}. ${s}`).join('\n');
        return `${header}\n${lines}`;

    }).join('\n\n');

}

export const SaveToTestRailModal = ({ onHide, phases, runSummary, show, testPlan, url }) => {

    const { api } = useApp();

    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [sections, setSections] = useState([]);

    const [projectId, setProjectId] = useState('');
    const [suiteId, setSuiteId] = useState('');
    const [sectionId, setSectionId] = useState('');

    const [title, setTitle] = useState('');
    const [steps, setSteps] = useState('');
    const [expected, setExpected] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {

        if (!show) return;

        setError(null);
        setSuccess(null);
        setTitle(defaultTitleFromPlan(testPlan, url));
        setSteps(formatStepsFromPhases(phases));
        setExpected(runSummary || '');

        api.get('/api/testrail/projects')
            .then(d => setProjects(unwrap(d, 'projects')))
            .catch(e => setError(e.err || e.message));

    }, [show, api, testPlan, url, phases, runSummary]);

    useEffect(() => {

        setSuiteId(''); setSectionId(''); setSections([]);
        if (!projectId) {

            setSuites([]); return;

        }

        api.get(`/api/testrail/projects/${projectId}/suites`)
            .then(d => setSuites(unwrap(d, 'suites')))
            .catch(e => setError(e.err || e.message));

    }, [api, projectId]);

    useEffect(() => {

        setSectionId('');
        if (!projectId || !suiteId) {

            setSections([]); return;

        }

        api.get(`/api/testrail/projects/${projectId}/sections?suite_id=${suiteId}`)
            .then(d => setSections(unwrap(d, 'sections')))
            .catch(e => setError(e.err || e.message));

    }, [api, projectId, suiteId]);

    const submit = async () => {

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        const payload = { title };
        if (steps && steps.trim().length) payload.custom_steps = steps;
        if (expected && expected.trim().length) payload.custom_expected = expected;

        try {

            const data = await api.post(`/api/testrail/sections/${sectionId}/cases`, payload);
            setSuccess(`Created TestRail case #${data?.id}.`);

        } catch (err) {

            setError(err.err || err.message);

        } finally {

            setSubmitting(false);

        }

    };

    const canSubmit = !submitting && !!sectionId && !!title.trim();

    return (

        <Modal centered onHide={onHide} show={show} size="lg">

            <Modal.Header closeButton>
                <Modal.Title>
                    Save as TestRail case
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
                )}
                {success && (
                <Alert dismissible onClose={() => setSuccess(null)} variant="success">
                    {success}
                </Alert>
                )}

                <Row className="g-2">
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Project
                            </Form.Label>
                            <Form.Select required onChange={e => setProjectId(e.target.value)} value={projectId}>
                                <option value="">
                                    Select…
                                </option>
                                {projects.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Suite
                            </Form.Label>
                            <Form.Select required disabled={!suites.length} onChange={e => setSuiteId(e.target.value)} value={suiteId}>
                                <option value="">
                                    Select…
                                </option>
                                {suites.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Section
                            </Form.Label>
                            <Form.Select required disabled={!sections.length} onChange={e => setSectionId(e.target.value)} value={sectionId}>
                                <option value="">
                                    Select…
                                </option>
                                {sections.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>
                        Title
                    </Form.Label>
                    <Form.Control required onChange={e => setTitle(e.target.value)} value={title} />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>
                        Steps
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        onChange={e => setSteps(e.target.value)}
                        rows={8}
                        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                        value={steps}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>
                        Expected result
                        <small className="text-muted">
                            (optional, populated from the AI's summary)
                        </small>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        onChange={e => setExpected(e.target.value)}
                        rows={3}
                        value={expected}
                    />
                </Form.Group>

            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} variant="outline-secondary">
                    Close
                </Button>
                <Button disabled={!canSubmit} onClick={submit} variant="primary">
                    {submitting ? (
                        <>
                            <Spinner animation="border" as="span" className="me-2" size="sm" />
                            Saving…
                        </>
) : 'Save case'}
                </Button>
            </Modal.Footer>

        </Modal>

    );

};
